import { formatDatasetName, datasetSubtypeLabel } from '../../../helpers';
import { datasetTypeIconUrl } from './datasetTypeIcon';

/**
 * Collapses a relationship graph onto the dataset parent hierarchy: only top-level datasets remain
 * as nodes, and every field-level relationship recorded on a child dataset is rolled up to its
 * top-level parent. This is what lets an API datasource's graph show endpoints (parents) rather
 * than their request/response/parameter children (where the fields, and thus the relationships,
 * actually live).
 *
 * Relationships internal to a single parent (both ends roll up to the same dataset) are dropped —
 * they are only relevant when drilling into that parent's own Dataset view. Edges whose endpoint
 * is outside the current scope (its parent is not among the nodes) are dropped too, matching how
 * the graph already ignores edges to absent nodes. All field-level pairs between the same pair of
 * parents are merged into a single edge so the edge popup lists the aggregated relationships.
 *
 * @param {Array<Object>} nodes - Datasets in scope, each carrying `parent_dataset_id` (from v_dataset).
 * @param {Array<Object>} edges - Denormalized relationships, with `from_dataset_id`/`to_dataset_id`.
 * @returns {{ nodes: Array<Object>, edges: Array<Object> }} Parent-only nodes and rolled-up edges.
 */
const rollUpRelationshipsToParent = (nodes, edges) => {
  // Index datasets by id and memoize each dataset's top-level (root) ancestor.
  const datasetById = new Map(nodes.map(node => [node.dataset_id, node]));
  const rootCache = new Map();
  const rootOf = id => {
    if (rootCache.has(id)) return rootCache.get(id);

    let current = datasetById.get(id);
    const seen = new Set();
    // Walk up parent_dataset_id until a top-level dataset (no parent) is reached. `seen` guards
    // against a malformed cycle in the data.
    while (current && current.parent_dataset_id != null && !seen.has(current.dataset_id)) {
      seen.add(current.dataset_id);
      current = datasetById.get(current.parent_dataset_id);
    }

    const rootId = current ? current.dataset_id : undefined;
    rootCache.set(id, rootId);
    return rootId;
  };

  // Only top-level datasets remain as nodes.
  const rolledNodes = nodes.filter(node => node.parent_dataset_id == null);

  // Remap each edge's endpoints to their root parent, then merge edges sharing the same parent pair.
  const mergedByPair = new Map();
  edges.forEach(edge => {
    const src = rootOf(edge.from_dataset_id);
    const tgt = rootOf(edge.to_dataset_id);

    // Drop edges whose endpoint is out of scope, and relationships internal to a single parent.
    if (src == null || tgt == null || src === tgt) return;

    const key = `${src}-${tgt}`;
    const existing = mergedByPair.get(key);

    if (existing) {
      // Aggregate the field-level pairs onto the already-created parent-to-parent edge.
      existing.fields = [...existing.fields, ...edge.fields];
    } else {
      // Rewrite the endpoints and their denormalized display fields to the parent datasets.
      const sourceParent = datasetById.get(src);
      const targetParent = datasetById.get(tgt);
      mergedByPair.set(key, {
        ...edge,
        relationship_name: key,
        from_dataset_id: src,
        from_dataset_name: formatDatasetName(
          sourceParent.dataset_name,
          datasetSubtypeLabel(
            sourceParent.dataset_subtype_code,
            sourceParent.dataset_subtype_name,
            sourceParent.dataset_type_code
          )
        ),
        from_dataset_type_name: sourceParent.dataset_type_name,
        from_dataset_description: sourceParent.dataset_description,
        from_dataset_group_id: sourceParent.dataset_group_id,
        from_datasource_id: sourceParent.datasource_id,
        from_system_id: sourceParent.system_id,
        to_dataset_id: tgt,
        to_dataset_name: formatDatasetName(
          targetParent.dataset_name,
          datasetSubtypeLabel(
            targetParent.dataset_subtype_code,
            targetParent.dataset_subtype_name,
            targetParent.dataset_type_code
          )
        ),
        to_dataset_type_name: targetParent.dataset_type_name,
        to_dataset_description: targetParent.dataset_description,
        to_dataset_group_id: targetParent.dataset_group_id,
        to_datasource_id: targetParent.datasource_id,
        to_system_id: targetParent.system_id,
      });
    }
  });

  return { nodes: rolledNodes, edges: [...mergedByPair.values()] };
};

/**
 * Turns fetched relationship data into the element array (nodes + edges + compound group/system
 * nodes) consumed by the Cytoscape graph.
 * @param {{ nodes: Array<Object>, edges: Array<Object> }} data
 * @param {boolean} [rollupToParent=false] - When true, collapse the graph onto the dataset parent
 *   hierarchy via {@link rollUpRelationshipsToParent} (used by the Datasource/DatasetGroup overview).
 * @param {boolean} [nestUnderParentDataset=false] - When true, a node that has an immediate parent
 *   dataset (`parent_dataset_id` + `parent_dataset_name`) is wrapped in a compound node for that
 *   parent, so the dataset hierarchy is visible (used by the Dataset view).
 * @returns {Array<Object>|undefined} Cytoscape elements, or undefined when no data.
 */
export const formatRelationships = (
  data,
  rollupToParent = false,
  nestUnderParentDataset = false
) => {
  if (data) {
    let nodes = data.nodes;
    let edges = data.edges;

    if (rollupToParent) {
      ({ nodes, edges } = rollUpRelationshipsToParent(nodes, edges));
    }

    let compoundNodesDataset = [];
    let compoundNodesLvl1 = [];
    let compoundNodesLvl2 = [];

    let fieldCountMax = Number.NEGATIVE_INFINITY;
    let rowCountMax = Number.NEGATIVE_INFINITY;
    let diskSizeMax = Number.NEGATIVE_INFINITY;

    let formattedNodes = nodes.map(node => {
      const relationshipCount = edges.filter(
        x => x.from_dataset_id === node.dataset_id || x.to_dataset_id === node.dataset_id
      ).length;

      const fieldCount = node.field_count;
      const rowCount = parseInt(node.row_count) || Number.NEGATIVE_INFINITY;
      const diskSize = parseInt(node.disk_size) || Number.NEGATIVE_INFINITY;

      if (fieldCountMax < fieldCount) {
        fieldCountMax = fieldCount;
      }
      if (rowCountMax < rowCount) {
        rowCountMax = rowCount;
      }
      if (diskSizeMax < diskSize) {
        diskSizeMax = diskSize;
      }

      compoundNodesLvl1.push({
        data: {
          id: 'dataset_group-' + node.dataset_group_id,
          parent: 'system-' + node.system_id,
          label: node.datasource_name + ' - ' + node.dataset_group_name,
          type: 'compound',
        },
      });

      compoundNodesLvl2.push({
        data: {
          id: 'system-' + node.system_id,
          label: node.system_name,
          type: 'compound',
        },
      });

      // In the Dataset view, wrap a node that has an immediate parent dataset in a compound for
      // that parent (nested under the group box), so the dataset hierarchy is shown. Otherwise the
      // node sits directly in its dataset group as before.
      const hasParentDataset =
        nestUnderParentDataset && node.parent_dataset_id != null && node.parent_dataset_name;
      const nodeParentId = hasParentDataset
        ? 'dataset-' + node.parent_dataset_id
        : 'dataset_group-' + node.dataset_group_id;

      if (hasParentDataset) {
        compoundNodesDataset.push({
          data: {
            id: 'dataset-' + node.parent_dataset_id,
            parent: 'dataset_group-' + node.dataset_group_id,
            label: formatDatasetName(
              node.parent_dataset_name,
              datasetSubtypeLabel(
                node.parent_dataset_subtype_code,
                node.parent_dataset_subtype_name,
                node.parent_dataset_type_code
              )
            ),
            type: 'compound',
          },
        });
      }

      return {
        data: {
          id: node.dataset_id,
          parent: nodeParentId,
          dataset_group_id: node.dataset_group_id,
          datasource_id: node.datasource_id,
          system_id: node.system_id,
          label: formatDatasetName(
            node.dataset_name,
            datasetSubtypeLabel(
              node.dataset_subtype_code,
              node.dataset_subtype_name,
              node.dataset_type_code
            )
          ),
          // Name and subtype kept alongside `label` so the popup title and the graph's filter can
          // render the subtype as a pill (the cytoscape `label` itself must stay a plain string).
          // Resolved through datasetSubtypeLabel like `label` is, so both show a pill only where the
          // subtype says something the dataset type does not -- an API endpoint's method. A Power BI
          // item's subtype mirrors its type, so it resolves to nothing and the type icon shows
          // instead.
          dataset_name: node.dataset_name,
          dataset_subtype_name: datasetSubtypeLabel(
            node.dataset_subtype_code,
            node.dataset_subtype_name,
            node.dataset_type_code
          ),
          dataset_subtype_operation: node.dataset_subtype_operation,
          datasource_type_code: node.datasource_type_code,
          dataset_description: node.dataset_description,
          dataset_type_name: node.dataset_type_name,
          // Drawn inside the node by the graph stylesheet; undefined when the type has no icon.
          typeIcon: datasetTypeIconUrl(node.dataset_type_name),
          field_count: node.field_count,
          row_count: node.row_count,
          disk_size: node.disk_size,
          attribute_ratio: node.attribute_ratio,
          relationship_count: relationshipCount,
          type: 'node',
          object_type: node.dataset_type_name,
          object_name: formatDatasetName(
            node.dataset_name,
            datasetSubtypeLabel(
              node.dataset_subtype_code,
              node.dataset_subtype_name,
              node.dataset_type_code
            )
          ),
          object_icon: node.dataset_type_name,
          object_description: node.dataset_description,
        },
        classes: 'node',
      };
    });

    compoundNodesDataset = [
      ...new Map(compoundNodesDataset.map(item => [item.data.id, item])).values(),
    ];
    compoundNodesLvl1 = [...new Map(compoundNodesLvl1.map(item => [item.data.id, item])).values()];
    compoundNodesLvl2 = [...new Map(compoundNodesLvl2.map(item => [item.data.id, item])).values()];

    // The group/system boxes are only drawn when more than one dataset group is present; with a
    // single group the graph stays flat. Parent-dataset boxes are always kept, but when the group
    // box is hidden they are promoted to the top level (their group parent is removed).
    const showGroups = compoundNodesLvl1.length > 1;

    if (!showGroups) {
      compoundNodesDataset = compoundNodesDataset.map(({ data: { parent, ...rest } }) => ({
        data: rest,
      }));
    }

    formattedNodes = formattedNodes.map(node => {
      // Collapse the group box for single-group graphs, but never drop a parent-dataset box.
      const nestedUnderDataset =
        typeof node.data.parent === 'string' && node.data.parent.startsWith('dataset-');
      if (!showGroups && !nestedUnderDataset) {
        delete node.data.parent;
      }
      return {
        ...node,
        data: {
          ...node.data,
          field_count_max: fieldCountMax,
          row_count_max: rowCountMax,
          disk_size_max: diskSizeMax,
        },
      };
    });

    formattedNodes = [
      ...formattedNodes,
      ...compoundNodesDataset,
      ...(showGroups ? [...compoundNodesLvl1, ...compoundNodesLvl2] : []),
    ];

    const formattedEdges = edges.map(edge => {
      return {
        data: {
          id: edge.relationship_name, // edge.relationship_id,
          source: edge.from_dataset_id,
          target: edge.to_dataset_id,
          fields: edge.fields,
          field_list: `|${edge.fields.map(field => field.from_field_name + '|' + field.to_field_name)}|`,
          source_field_name: edge.from_field_name,
          source_dataset_name: edge.from_dataset_name,
          source_dataset_type_name: edge.from_dataset_type_name,
          source_dataset_description: edge.from_dataset_description,
          source_datatype_name: edge.from_datatype_name,
          source_datatype_category: edge.from_datatype_category,
          source_datatype_fullname: edge.from_datatype_fullname,
          source_attribute_description: edge.from_attribute_description,
          source_field_id: edge.from_field_id,
          source_dataset_group_id: edge.from_dataset_group_id,
          source_datasource_id: edge.from_datasource_id,
          source_system_id: edge.from_system_id,
          target_field_name: edge.to_field_name,
          target_dataset_name: edge.to_dataset_name,
          target_dataset_type_name: edge.to_dataset_type_name,
          target_dataset_description: edge.to_dataset_description,
          target_datatype_name: edge.to_datatype_name,
          target_datatype_category: edge.to_datatype_category,
          target_datatype_fullname: edge.to_datatype_fullname,
          target_attribute_description: edge.to_attribute_description,
          target_field_id: edge.to_field_id,
          target_dataset_group_id: edge.to_dataset_group_id,
          target_datasource_id: edge.to_datasource_id,
          target_system_id: edge.to_system_id,
          type: 'edge',
          relationship_type: edge.relationship_type,
          object_type: 'Field',
          object_name: edge.from_field_name,
          object_icon: edge.from_datatype_category,
          object_description: edge.from_attribute_description,
        },
      };
    });
    return [...formattedNodes, ...formattedEdges];
  }
};
